import React, { useState, useEffect, useRef } from "react";
import { X, Edit2, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import useBankDetailsApi, { getAuthToken } from "../hooks/Usebankdetailsapi"

const BankDetailsCard = ({ isOpen, onClose }) => {
const {
  fetchBankDetails: fetchBankDetailsAPI,
  updateBankDetails: updateBankDetailsAPI,
} = useBankDetailsApi();

  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingBank, setFetchingBank] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  const [form, setForm] = useState({
    ifscCode: "",
    accountNumber: "",
  });

  const [fetchedBankInfo, setFetchedBankInfo] = useState({
    accountHolderName: "",
    bankName: "",
    branch: "",
    branchAddress: "",
    city: "",
    district: "",
    state: "",
  });

  const [errors, setErrors] = useState({});

  // 🛑 Synchronous in-flight guards (block double-submit / StrictMode double-fire)
  const submitInFlightRef = useRef(false);
  const fetchInFlightRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      loadBankDetails();
    }
  }, [isOpen]);

  // Reset guards whenever the modal closes so a fresh open starts clean
  useEffect(() => {
    if (!isOpen) {
      submitInFlightRef.current = false;
      fetchInFlightRef.current = false;
      setIsEditing(false);
      setIsAdding(false);
      setIsFetched(false);
      setForm({ ifscCode: "", accountNumber: "" });
      setFetchedBankInfo({
        accountHolderName: "",
        bankName: "",
        branch: "",
        branchAddress: "",
        city: "",
        district: "",
        state: "",
      });
      setErrors({});
      setSubmitting(false);
      setFetchingBank(false);
    }
  }, [isOpen]);

  const loadBankDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchBankDetailsAPI();
      if (response && response.accountNumber) {
        setAccountDetails(response);
      } else {
        setAccountDetails(null);
      }
    } catch (error) {
      console.error("Failed to fetch bank details:", error);
      setAccountDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.accountNumber?.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (!/^\d{9,18}$/.test(form.accountNumber.replace(/\s/g, ""))) {
      newErrors.accountNumber = "Account number must be 9-18 digits";
    }
    if (!form.ifscCode?.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifscCode)) {
      newErrors.ifscCode = "Invalid IFSC code format";
    }
    return newErrors;
  };

  const handleChange = (field, value) => {
    let processed = value;
    if (field === "accountNumber") {
      processed = value.replace(/\D/g, "");
    } else if (field === "ifscCode") {
      processed = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 11);
    }
    setForm((prev) => ({ ...prev, [field]: processed }));
    if (errors[field]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[field];
        return e;
      });
    }
  };

  const handleFetchBankInfo = async () => {
    // 🛑 Block duplicate fetch calls
    if (fetchInFlightRef.current) {
      console.log("[BankDetailsCard] Fetch already in progress, ignoring duplicate call");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before continuing");
      return;
    }

    fetchInFlightRef.current = true;
    setFetchingBank(true);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/lenskart/get-Bank-Details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            ifscCode: form.ifscCode,
            accountNumber: form.accountNumber,
          }),
        }
      );

      const result = await response.json();

      if (result.error || result.account_status === "INVALID") {
        toast.error(
          result.details || result.error || "Bank account verification failed"
        );
        return;
      }

      if (!result.verificationResult) {
        throw new Error("We are currently facing an issue. Please try later.");
      }

      const { verificationResult } = result;

      if (verificationResult.account_status === "INVALID") {
        toast.error("Bank account verification failed: Invalid account");
        return;
      }

      if (!verificationResult.name_at_bank && !verificationResult.bank_name) {
        toast.error(
          "Unable to fetch complete bank details. Please check your account number and IFSC code."
        );
        return;
      }

      setFetchedBankInfo({
        accountHolderName: verificationResult.name_at_bank || "",
        bankName:
          verificationResult.bank_name ||
          verificationResult.ifsc_details?.bank ||
          "",
        branch:
          verificationResult.branch ||
          verificationResult.ifsc_details?.branch ||
          "",
        branchAddress: verificationResult.ifsc_details?.address || "",
        city:
          verificationResult.city ||
          verificationResult.ifsc_details?.city ||
          "",
        district: verificationResult.ifsc_details?.city || "",
        state: verificationResult.ifsc_details?.state || "",
      });

      setIsFetched(true);
      toast.success("Bank details fetched successfully!");
    } catch (error) {
      console.error("Fetch bank details error:", error);
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message ||
        "We are currently facing some issues. Please try again later.";
      toast.error(serverMessage);
    } finally {
      fetchInFlightRef.current = false;
      setFetchingBank(false);
    }
  };

  const handleSubmit = async () => {
  if (submitInFlightRef.current) return;

  if (!isFetched) {
    toast.error("Please fetch bank details first");
    return;
  }

  submitInFlightRef.current = true;
  setSubmitting(true);

  try {
    const response = await updateBankDetailsAPI(
      {
        name: fetchedBankInfo.accountHolderName.trim(),
        accountNumber: form.accountNumber,
        ifscCode: form.ifscCode,
        bankName: fetchedBankInfo.bankName.trim(),
        branchName: fetchedBankInfo.branch.trim(),
        branchAddress: fetchedBankInfo.branchAddress.trim(),
        city: fetchedBankInfo.city.trim(),
        district: fetchedBankInfo.district.trim(),
        state: fetchedBankInfo.state.trim(),
      },
      !!accountDetails
    );

    // 🛡️ Handle the case where the API returns HTTP 200 with success: false
    if (response && response.success === false) {
      const serverMessage =
        response.message ||
        response.error ||
        response.details ||
        "Failed to save bank details.";
      toast.error(serverMessage);
      return; // ⛔ do NOT call loadBankDetails, do NOT call handleCancel
    }

    // ✅ Only reached on a genuine success
    toast.success("Bank details saved successfully!");
    await loadBankDetails();
    handleCancel();
  } catch (error) {
    const serverMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.details ||
      error?.message ||
      "Failed to save bank details. Please try again.";
    toast.error(serverMessage);
    // ⛔ no loadBankDetails, no handleCancel
  } finally {
    submitInFlightRef.current = false;
    setSubmitting(false);
  }
};

  const handleStartEdit = () => {
    setForm({
      ifscCode: accountDetails?.ifscCode || "",
      accountNumber: "",
    });
    setFetchedBankInfo({
      accountHolderName: "",
      bankName: "",
      branch: "",
      branchAddress: "",
      city: "",
      district: "",
      state: "",
    });
    setIsFetched(false);
    setErrors({});
    setIsEditing(true);
  };

  const handleStartAdd = () => {
    setForm({ ifscCode: "", accountNumber: "" });
    setFetchedBankInfo({
      accountHolderName: "",
      bankName: "",
      branch: "",
      branchAddress: "",
      city: "",
      district: "",
      state: "",
    });
    setIsFetched(false);
    setErrors({});
    setIsAdding(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsAdding(false);
    setIsFetched(false);
    setForm({ ifscCode: "", accountNumber: "" });
    setFetchedBankInfo({
      accountHolderName: "",
      bankName: "",
      branch: "",
      branchAddress: "",
      city: "",
      district: "",
      state: "",
    });
    setErrors({});
  };

  const handleChangeDetails = () => {
    setIsFetched(false);
    setFetchedBankInfo({
      accountHolderName: "",
      bankName: "",
      branch: "",
      branchAddress: "",
      city: "",
      district: "",
      state: "",
    });
  };

  if (!isOpen) return null;

  const showForm = isAdding || isEditing;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 bg-[#F2F2FD]">
          <h2 className="text-lg font-bold text-gray-900">Bank Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="h-px bg-gray-100" />

        <div className="px-6 py-5">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : showForm ? (
            <div className="flex flex-col max-h-[65vh]">
              {!isFetched ? (
                <>
                  {/* Scrollable inputs */}
                  <div className="space-y-4 overflow-y-auto pr-1 pt-3 flex-1">
                    {/* IFSC Code */}
                    <div className="relative">
                      <label className="absolute -top-2 left-3 text-xs text-gray-500 bg-white px-1">
                        IFSC Code *
                      </label>
                      <input
                        type="text"
                        value={form.ifscCode}
                        onChange={(e) =>
                          handleChange("ifscCode", e.target.value)
                        }
                        maxLength={11}
                        placeholder=""
                        className={`w-full border rounded-xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:ring-1 ${
                          errors.ifscCode
                            ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                            : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        }`}
                      />
                      {errors.ifscCode && (
                        <p className="text-xs text-red-500 mt-1 ml-1">
                          {errors.ifscCode}
                        </p>
                      )}
                    </div>

                    {/* Account Number */}
                    <div className="relative">
                      <label className="absolute -top-2 left-3 text-xs text-gray-500 bg-white px-1">
                        Account Number *
                      </label>
                      <input
                        type="text"
                        value={form.accountNumber}
                        onChange={(e) =>
                          handleChange("accountNumber", e.target.value)
                        }
                        placeholder=""
                        className={`w-full border rounded-xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:ring-1 ${
                          errors.accountNumber
                            ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                            : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        }`}
                      />
                      {errors.accountNumber && (
                        <p className="text-xs text-red-500 mt-1 ml-1">
                          {errors.accountNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Always-visible buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleCancel}
                      disabled={fetchingBank}
                      className="flex-1 py-3 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFetchBankInfo}
                      disabled={fetchingBank || fetchInFlightRef.current}
                      className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {fetchingBank && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      {fetchingBank ? "Fetching..." : "Fetch Details"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Scrollable verified info */}
                  <div className="overflow-y-auto pr-1 pt-1 flex-1">
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 mb-3">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <p className="text-xs text-green-700 font-medium">
                        Account verified successfully
                      </p>
                    </div>

                    <div className="space-y-2.5 bg-gray-50 rounded-xl p-4">
                      {[
                        {
                          label: "Account Holder",
                          value: fetchedBankInfo.accountHolderName,
                        },
                        { label: "Account Number", value: form.accountNumber },
                        { label: "IFSC Code", value: form.ifscCode },
                        { label: "Bank Name", value: fetchedBankInfo.bankName },
                        { label: "Branch", value: fetchedBankInfo.branch },
                        {
                          label: "Branch Address",
                          value: fetchedBankInfo.branchAddress,
                        },
                        { label: "City", value: fetchedBankInfo.city },
                        { label: "District", value: fetchedBankInfo.district },
                        { label: "State", value: fetchedBankInfo.state },
                      ].map(({ label, value }, i, arr) => (
                        <div
                          key={label}
                          className={`flex justify-between items-start py-2 ${
                            i < arr.length - 1 ? "border-b border-gray-200" : ""
                          }`}
                        >
                          <span className="text-xs text-gray-500 shrink-0">
                            {label}
                          </span>
                          <span className="text-xs font-semibold text-gray-800 text-right ml-4 max-w-[55%] break-words">
                            {value || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Always-visible buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleChangeDetails}
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Change
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || submitInFlightRef.current}
                      className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      {submitting ? "Saving..." : "Save Details"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : accountDetails ? (
            /* View: Bank details exist */
            <div>
              <div className="space-y-3 mb-5">
                {[
                  { label: "Account Holder", value: accountDetails.name },
                  {
                    label: "Account Number",
                    value: `XXXX${accountDetails.accountNumber.slice(-4)}`,
                  },
                  { label: "IFSC Code", value: accountDetails.ifscCode },
                  { label: "Bank Name", value: accountDetails.bankName },
                  { label: "Branch", value: accountDetails.branchName },
                  { label: "City", value: accountDetails.city },
                  { label: "State", value: accountDetails.state },
                ].map(({ label, value }, i, arr) => (
                  <div
                    key={label}
                    className={`flex justify-between items-center py-2 ${
                      i < arr.length - 1 ? "border-b border-gray-100" : ""
                    }`}
                  >
                    <span className="text-sm text-gray-500 shrink-0">
                      {label}
                    </span>
                    <span className="text-sm font-semibold text-gray-800 text-right min-w-0 ml-4 max-w-[55%] break-words">
                      {value || "—"}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={handleStartEdit}
                className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit Bank Details
              </button>
            </div>
          ) : (
            /* Empty: No bank details */
            <div className="flex flex-col items-center py-4">
              <p className="text-gray-500 text-sm mb-5">
                No bank details added yet
              </p>
              <button
                onClick={handleStartAdd}
                className="px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                Add Bank Details
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankDetailsCard;